import React, { useEffect, useRef, useState } from 'react';
import { pickChangedShallow } from '@/utils/patchFromDirty';
import { createPortal } from 'react-dom';
import { X, Check, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AlertContainer, Button } from '@/components/ui';
import { useModal } from '@/hooks';
import { useCreatePlan, usePatchPlan } from '@/services/mutations/company';

const COMPANY_PLAN_PRICE_KEYS = new Set(['base_cost', 'subsidy_per_employee']);

const PRICE_UPDATE_CONFIRM_MESSAGE =
	'Isso irá atualizar o preço da assinatura de todos os usuários/empresas que usam este plano ou assinatura e será efetivada na renovação da assinatura de cada usuário (cada um possui uma data diferente).';

interface CompanyPlanModalProps {
	isOpen: boolean;
	onClose: () => void;
	plan?: Companies.Plan | null;
}

const formatCostForDisplay = (value: string) => {
	const num = parseFloat(value);
	if (isNaN(num)) return '';
	return num.toLocaleString('pt-BR', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const SlidingToggle = ({
	label,
	value,
	onChange,
	options,
}: {
	label: string;
	value: boolean;
	onChange: (val: boolean) => void;
	options: { label: string; value: boolean }[];
}) => {
	const activeIndex = options.findIndex((o) => o.value === value);
	const count = options.length;
	const toggleLabelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400 block mb-2';

	return (
		<div className="w-full">
			<label className={toggleLabelClass}>{label}</label>
			<div className="relative flex w-full rounded-lg bg-stone-100 p-1">
				<div
					className="absolute bottom-1 top-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
					style={{
						width: `calc((100% - 8px) / ${count})`,
						left: `calc(4px + ${activeIndex} * ((100% - 8px) / ${count}))`,
					}}
				/>
				{options.map((opt) => (
					<button
						key={String(opt.value)}
						type="button"
						onClick={() => onChange(opt.value)}
						className={cn(
							'z-10 flex-1 cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-[10px] font-black uppercase tracking-tight outline-none transition-colors duration-200 focus:outline-none',
							value === opt.value
								? 'text-stone-900'
								: 'text-stone-400 hover:text-stone-600',
						)}
					>
						{opt.label}
					</button>
				))}
			</div>
		</div>
	);
};

export const CompanyPlanModal = ({
	isOpen,
	onClose,
	plan,
}: CompanyPlanModalProps) => {
	const { openModal, setOnConfirm, setConfirmText, setCancelText } =
		useModal();
	const [formData, setFormData] = useState({
		name: '',
		base_cost: '',
		subsidy_per_employee: '',
		max_employees: '',
		description: '',
		active: true,
	});

	const [errors, setErrors] = useState<Record<string, string>>({});
	const initialPlanPayloadRef = useRef<Companies.CreatePlan | null>(null);

	const { mutateAsync: createPlan, isPending: isCreating } = useCreatePlan(
		() => onClose(),
	);
	const { mutateAsync: patchPlan, isPending: isPatching } = usePatchPlan(() =>
		onClose(),
	);
	const isPending = isCreating || isPatching;

	const parseCostToApi = (value: string) => {
		const digits = value.replace(/\D/g, '');
		if (digits.length <= 2) return `${parseInt(digits || '0', 10)}.00`;
		return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
	};

	useEffect(() => {
		if (!isOpen) {
			initialPlanPayloadRef.current = null;
			return;
		}
		setErrors({});
		if (plan) {
			setFormData({
				name: plan.name,
				base_cost: formatCostForDisplay(plan.base_cost || '0'),
				subsidy_per_employee: formatCostForDisplay(
					plan.subsidy_per_employee || '0',
				),
				max_employees: String(plan.max_employees),
				description: plan.description || '',
				active: plan.active ?? true,
			});
			if (plan.id) {
				const payload: Companies.CreatePlan = {
					name: plan.name.trim(),
					base_cost: parseCostToApi(
						formatCostForDisplay(plan.base_cost || '0'),
					),

					subsidy_per_employee: parseCostToApi(
						formatCostForDisplay(plan.subsidy_per_employee || '0'),
					),
					max_employees: plan.max_employees,
					currency: 'BRL',
					active: plan.active ?? true,
				};
				if (plan.description?.trim()) {
					payload.description = plan.description.trim();
				}
				initialPlanPayloadRef.current = payload;
			} else {
				initialPlanPayloadRef.current = null;
			}
		} else {
			setFormData({
				name: '',
				base_cost: '',
				subsidy_per_employee: '',
				max_employees: '',
				description: '',
				active: true,
			});
			initialPlanPayloadRef.current = null;
		}
	}, [isOpen, plan]);

	const validate = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.name.trim()) newErrors.name = 'Obrigatório';
		else if (formData.name.length < 2)
			newErrors.name = 'Mínimo 2 caracteres';

		const baseMatch = formData.base_cost.match(/^[\d.,]+$/);
		if (!formData.base_cost.trim()) newErrors.base_cost = 'Obrigatório';
		else if (!baseMatch)
			newErrors.base_cost = 'Use o formato 0,00 (ex: 49,90)';

		const maxEmp = parseInt(formData.max_employees, 10);
		if (!formData.max_employees.trim())
			newErrors.max_employees = 'Obrigatório';
		else if (isNaN(maxEmp) || maxEmp < 1)
			newErrors.max_employees = 'Mínimo 1 funcionário';

		const subsidyMatch = formData.subsidy_per_employee.match(/^[\d.,]+$/);
		if (!formData.subsidy_per_employee.trim())
			newErrors.subsidy_per_employee = 'Obrigatório';
		else if (!subsidyMatch)
			newErrors.subsidy_per_employee = 'Use o formato 0,00 (ex: 5,00)';

		if (formData.description.trim() && formData.description.length < 3)
			newErrors.description = 'Mínimo 3 caracteres';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validate()) return;

		const baseApi = parseCostToApi(formData.base_cost);
		const subsidyApi = parseCostToApi(formData.subsidy_per_employee);
		const payload: Companies.CreatePlan = {
			name: formData.name.trim(),
			base_cost: baseApi,
			subsidy_per_employee: subsidyApi,
			max_employees: parseInt(formData.max_employees, 10),
			currency: 'BRL',
			active: formData.active,
		};

		if (formData.description.trim()) {
			payload.description = formData.description.trim();
		}

		if (plan?.id) {
			const initial = initialPlanPayloadRef.current;
			const body = pickChangedShallow(
				{ ...payload } as Record<string, unknown>,
				initial ? ({ ...initial } as Record<string, unknown>) : null,
			) as Companies.UpdatePlan;
			if (Object.keys(body).length === 0) {
				onClose();
				return;
			}
			const affectsSubscriptionPrice = Object.keys(body).some((k) =>
				COMPANY_PLAN_PRICE_KEYS.has(k),
			);
			if (affectsSubscriptionPrice) {
				setConfirmText('Confirmar atualização');
				setCancelText('Cancelar');
				openModal({
					title: 'Confirmar alteração de preço',
					content: (
						<AlertContainer
							message={PRICE_UPDATE_CONFIRM_MESSAGE}
						/>
					),
				});
				setOnConfirm(() => {
					void patchPlan({ id: plan.id, body });
				});
				return;
			}
			await patchPlan({ id: plan.id, body });
		} else {
			await createPlan(payload);
		}
	};

	if (!isOpen) return null;

	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';
	const inputBaseClass =
		'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none transition-all box-border disabled:opacity-50';

	const getFieldClass = (fieldName: string) =>
		cn(
			inputBaseClass,
			errors[fieldName]
				? 'border-rose-500 bg-rose-50/30 focus:border-rose-600'
				: 'border-stone-200 focus:border-stone-400 focus:bg-white',
		);

	const ErrorMsg = ({ field }: { field: string }) =>
		errors[field] ? (
			<span className="animate-in fade-in slide-in-from-top-1 ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
				<AlertCircle size={10} /> {errors[field]}
			</span>
		) : null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<form
					onSubmit={handleSave}
					noValidate
					className="flex flex-col overflow-hidden"
				>
					{/* Header do Modal */}
					<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
						<h2 className="text-lg font-black text-stone-900">
							{plan
								? 'Editar Plano Empresarial'
								: 'Novo Plano Empresarial'}
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="text-stone-400 transition-colors hover:text-stone-600"
						>
							<X size={20} />
						</button>
					</div>

					{/* Corpo do Modal */}
					<div className="flex-1 space-y-6 overflow-y-auto p-8">
						{/* Nome do Plano */}
						<div>
							<label className={labelClass}>NOME DO PLANO</label>
							<input
								type="text"
								value={formData.name}
								onChange={(e) =>
									setFormData({
										...formData,
										name: e.target.value,
									})
								}
								className={getFieldClass('name')}
								placeholder="Ex: Plano Básico"
							/>
							<ErrorMsg field="name" />
						</div>

						{/* Custo e Máx. Funcionários */}
						<div>
							<label className={labelClass}>
								CUSTO BASE (R$)
							</label>
							<input
								type="text"
								value={formData.base_cost}
								onChange={(e) => {
									const raw = e.target.value.replace(
										/\D/g,
										'',
									);
									const formatted =
										raw.length <= 2
											? raw
											: `${parseInt(
													raw.slice(0, -2),
													10,
											  ).toLocaleString(
													'pt-BR',
											  )},${raw.slice(-2)}`;
									setFormData({
										...formData,
										base_cost: formatted,
									});
								}}
								className={getFieldClass('base_cost')}
								placeholder="99,90"
							/>
							<ErrorMsg field="base_cost" />
						</div>

						<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
							<div>
								<label className={labelClass}>
									SUBSÍDIO POR FUNCIONÁRIO (R$)
								</label>
								<input
									type="text"
									value={formData.subsidy_per_employee}
									onChange={(e) => {
										const raw = e.target.value.replace(
											/\D/g,
											'',
										);
										const formatted =
											raw.length <= 2
												? raw
												: `${parseInt(
														raw.slice(0, -2),
														10,
												  ).toLocaleString(
														'pt-BR',
												  )},${raw.slice(-2)}`;
										setFormData({
											...formData,
											subsidy_per_employee: formatted,
										});
									}}
									className={getFieldClass(
										'subsidy_per_employee',
									)}
									placeholder="5,00"
								/>
								<ErrorMsg field="subsidy_per_employee" />
							</div>

							<div>
								<label className={labelClass}>
									MÁX. FUNCIONÁRIOS
								</label>
								<input
									type="number"
									min={1}
									value={formData.max_employees}
									onChange={(e) =>
										setFormData({
											...formData,
											max_employees: e.target.value,
										})
									}
									className={getFieldClass('max_employees')}
									placeholder="50"
								/>
								<ErrorMsg field="max_employees" />
							</div>
						</div>

						{/* Descrição */}
						<div>
							<label className={labelClass}>DESCRIÇÃO</label>
							<textarea
								value={formData.description}
								onChange={(e) =>
									setFormData({
										...formData,
										description: e.target.value,
									})
								}
								className={cn(
									getFieldClass('description'),
									'min-h-[80px] resize-y',
								)}
								placeholder="Descrição opcional do plano..."
								rows={3}
							/>
							<ErrorMsg field="description" />
						</div>

						<SlidingToggle
							label="Plano ativo e disponível?"
							value={formData.active}
							onChange={(val) =>
								setFormData({ ...formData, active: val })
							}
							options={[
								{ label: 'Sim', value: true },
								{ label: 'Não', value: false },
							]}
						/>
					</div>

					{/* Footer do Modal */}
					<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
						<button
							type="button"
							onClick={onClose}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button
							type="submit"
							text={
								isPending
									? 'Salvando...'
									: plan
									  ? 'Salvar alterações'
									  : 'Criar Plano'
							}
							disabled={isPending}
							className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};
